import { type PropsWithChildren } from 'react';

import { useGridPlotSize } from '../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { safeGet } from '../../core/utils/safe-get.js';
import {
  getHorizontalNumTicks,
  getVerticalNumTicks,
} from '../../core/utils/ticks.js';
import { XYChartAxesTicksContext } from '../context/axes/XYChartAxesTicks.context.js';
import { useAxesFormatter } from '../hooks/useAxesFormatter.js';
import { useVisibleAxesConfig } from '../hooks/useVisibleAxesConfig.js';
import { useVisibleAxesScales } from '../hooks/useVisibleAxesScales.js';
import type { AxisIdToFormatter } from '../types/xy-chart-internals.js';
import { getTicksFormattedByConfig } from '../utils/get-ticks-formatted-by-config.js';

export function XYChartAxesTicksProvider({ children }: PropsWithChildren) {
  const { width, height } = useGridPlotSize();
  const axisIdToFormatter: AxisIdToFormatter = useAxesFormatter();
  const config = useVisibleAxesConfig();
  const scales = useVisibleAxesScales();
  const horizontalNumTicks = getHorizontalNumTicks(width);
  const verticalNumTicks = getVerticalNumTicks(height);

  const topScale = scales.top;
  const topConfig = config.top;

  const topFormatter = topConfig
    ? safeGet(axisIdToFormatter, topConfig.id)
    : undefined;

  const topTicks = getTicksFormattedByConfig(
    topConfig,
    horizontalNumTicks,
    topScale,
    topFormatter,
  );

  const bottomScale = scales.bottom;
  const bottomConfig = config.bottom;

  const bottomFormatter = bottomConfig
    ? safeGet(axisIdToFormatter, bottomConfig.id)
    : undefined;

  const bottomTicks = getTicksFormattedByConfig(
    bottomConfig,
    horizontalNumTicks,
    bottomScale,
    bottomFormatter,
  );

  const leftScale = scales.left;
  const leftConfig = config.left;

  const leftFormatter = leftConfig
    ? safeGet(axisIdToFormatter, leftConfig.id)
    : undefined;

  const leftTicks = getTicksFormattedByConfig(
    leftConfig,
    verticalNumTicks,
    leftScale,
    leftFormatter,
  );

  const rightScale = scales.right;
  const rightConfig = config.right;

  const rightFormatter = rightConfig
    ? safeGet(axisIdToFormatter, rightConfig.id)
    : undefined;

  const rightTicks = getTicksFormattedByConfig(
    rightConfig,
    verticalNumTicks,
    rightScale,
    rightFormatter,
  );

  return (
    <XYChartAxesTicksContext.Provider
      value={{ topTicks, bottomTicks, leftTicks, rightTicks }}
    >
      {children}
    </XYChartAxesTicksContext.Provider>
  );
}
